import { buttonClass } from "@gmx/ui";
import { useQueryClient } from "@tanstack/react-query";
import { LinkIcon } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { FullPageLoader } from "../../components/FullPage";
import { authApi } from "../../lib/api";
import { adoptSession, safeNext, useAuth } from "../../lib/auth";
import { AuthHeading, AuthLayout } from "./AuthLayout";

/**
 * /sso?code=…&next=/path — the extension's "Open Grow My X" handoff.
 * Redeems the one-time code (POST /api/auth/handoff/redeem) for a cookie session.
 */
export function SsoPage() {
  const [params] = useSearchParams();
  const code = params.get("code");
  const next = safeNext(params.get("next"));
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { status } = useAuth();
  const started = useRef(false);
  const [failed, setFailed] = useState(!code);

  useEffect(() => {
    // Wait for the initial /me to settle so it can't race the new session.
    // One-time code: never redeem twice (StrictMode double-invokes effects).
    if (!code || started.current || status === "loading") return;
    started.current = true;
    authApi
      .redeemHandoff(code)
      .then(async (res) => {
        await adoptSession(qc, res.user);
        navigate(next, { replace: true });
      })
      .catch(() => setFailed(true));
  }, [code, next, navigate, qc, status]);

  if (!failed) return <FullPageLoader />;

  return (
    <AuthLayout title="Sign-in link expired">
      <div className="gfp-auth__icon gfp-auth__icon--warn">
        <LinkIcon size={22} />
      </div>
      <AuthHeading
        title="That sign-in link expired"
        lede="Links from the extension work once, for about a minute. Click “Open Grow My X” in the extension again, or sign in here."
      />
      <Link to="/login" className={buttonClass({ variant: "primary", size: "lg", block: true })}>
        Sign in
      </Link>
    </AuthLayout>
  );
}
